// src/pages/SearchPatient.jsx
import React, { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import medicalBg from '../assets/bb.jpg';
import leftArrow from '../assets/left.png';
import { FaSearch } from 'react-icons/fa';

// Mock patient directory for demo/search purposes.
// Replace this with a real API call (e.g. fetch('/api/patients?q=...')) when the backend is ready.
const MOCK_PATIENTS = [
  { id: 'PAT-00123', name: 'John Doe' },
  { id: 'PAT-00124', name: 'Nour Ali' },
  { id: 'PAT-00125', name: 'Fatma Khaled' },
  { id: 'PAT-00126', name: 'Mohamed Tarek' },
  { id: 'PAT-00127', name: 'Layla Said' },
];

const SearchPatient = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [error, setError] = useState('');
  const [isFocused, setIsFocused] = useState(false);

  const suggestions = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return [];
    return MOCK_PATIENTS.filter(
      (p) => p.id.toLowerCase().includes(term) || p.name.toLowerCase().includes(term)
    ).slice(0, 5);
  }, [query]);

  const goToPatient = (patient) => {
    navigate('/show-patient', { state: { patientId: patient.id, patientName: patient.name } });
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const term = query.trim();
    if (!term) {
      setError('Please enter a patient ID or name.');
      return;
    }

    const exactMatch =
      MOCK_PATIENTS.find((p) => p.id.toLowerCase() === term.toLowerCase()) ||
      (suggestions.length === 1 ? suggestions[0] : null);

    if (exactMatch) {
      setError('');
      goToPatient(exactMatch);
    } else {
      setError(`No patient record found for "${term}".`);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--bg)]" style={{ fontFamily: 'var(--font-body)' }}>
      {/* Top banner */}
      <div className="relative w-full h-56 overflow-hidden">
        <img src={medicalBg} alt="" className="w-full h-full object-cover" />
        <div
          className="absolute inset-0"
          style={{ background: 'linear-gradient(160deg, rgba(22,38,43,0.85) 0%, rgba(14,124,123,0.5) 100%)' }}
        />
        <Link
          to="/"
          className="absolute top-6 left-6 bg-white/90 backdrop-blur rounded-full p-2.5 shadow-md hover:bg-white transition-colors duration-200"
        >
          <img src={leftArrow} alt="Back to Home" className="w-5 h-5" />
        </Link>
      </div>

      {/* Search card */}
      <div className="relative -mt-24 z-10 bg-white border border-[var(--mist)] p-8 rounded-3xl shadow-xl w-full max-w-2xl mx-auto">
        <span
          className="block text-[var(--coral)] text-xs tracking-[0.2em] uppercase mb-2"
          style={{ fontFamily: 'var(--font-mono)' }}
        >
          Patient Records
        </span>
        <h1
          className="text-2xl md:text-3xl font-medium text-[var(--ink)] mb-6"
          style={{ fontFamily: 'var(--font-display)' }}
        >
          Find Patient Record
        </h1>

        <form onSubmit={handleSearch} autoComplete="off">
          <div className="relative">
            <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--ink)]/40 text-lg" />
            <input
              type="search"
              name="search"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                if (error) setError('');
              }}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setTimeout(() => setIsFocused(false), 120)}
              placeholder="Search by ID or name"
              className="w-full pl-12 pr-4 py-3 border border-[var(--mist)] rounded-xl text-base
                         focus:outline-none focus:ring-2 focus:ring-[var(--teal)] transition-shadow"
            />

            {/* Live suggestions */}
            {isFocused && suggestions.length > 0 && (
              <ul className="absolute left-0 right-0 mt-2 bg-white border border-[var(--mist)] rounded-xl shadow-lg overflow-hidden z-20">
                {suggestions.map((p) => (
                  <li key={p.id}>
                    <button
                      type="button"
                      onMouseDown={() => goToPatient(p)}
                      className="w-full text-left px-4 py-3 hover:bg-[var(--mist)]/40 transition-colors duration-150 flex items-center justify-between"
                    >
                      <span className="text-[var(--ink)] font-medium">{p.name}</span>
                      <span
                        className="text-[var(--ink)]/50 text-xs"
                        style={{ fontFamily: 'var(--font-mono)' }}
                      >
                        {p.id}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {error && <p className="text-[var(--coral)] text-sm mt-3">{error}</p>}

          <button
            type="submit"
            className="block mx-auto mt-8 bg-[var(--teal)] hover:bg-[var(--teal-deep)] text-white font-medium
                       py-3 px-12 rounded-full text-lg transition-colors duration-200"
          >
            Search
          </button>
        </form>
      </div>
    </div>
  );
};

export default SearchPatient;
